/*
 * Shared link sessions can't reach the tenant lookup or NMS (recon) APIs. The tenant lookups a shared page needs are
 * saved on the shared link when it's created (src/node/services/sharedLink/sharedLinkLookups.ts), keyed by API path,
 * and served from here instead. Global reference lookups (SHARED_PUBLIC_LOOKUP_PATHS) aren't saved; they're requested
 * from /api/ui/shared/lookups.
 */

import { SHARED_PUBLIC_LOOKUP_PATHS } from '@kentik/ui-shared/sharedLink/publicLookups';

type SavedLookups = Record<string, unknown>;

const SAVED_LOOKUP_PREFIXES = ['/api/ui/lookups/', '/api/ui/recon/'];

const PUBLIC_LOOKUP_KEYS = new Set<string>(SHARED_PUBLIC_LOOKUP_PATHS.map((path: string) => `/lookups${path}`));

// links created before lookups were saved request these from the shared routes, which serve only what the link uses
const SHARED_FALLBACK_KEYS = new Set<string>(['/recon/metrics']);

// callers of these endpoints expect an object rather than a list
const EMPTY_RESPONSES: SavedLookups = {
  '/lookups/devices': { devices: [], hasMore: false },
  '/recon/devices': { models: [], totalCount: 0 }
};

let savedLookups: SavedLookups = {};

// '/api/ui/lookups/countries?term=US' -> '/lookups/countries'
const toLookupKey = (path: string) => path.split('?')[0].replace(/^\/api\/ui/, '');

export const setSharedLinkLookups = (lookups?: SavedLookups | null) => {
  savedLookups = lookups && typeof lookups === 'object' ? lookups : {};
};

export const isSharedLinkLookupPath = (path: string) => SAVED_LOOKUP_PREFIXES.some((prefix) => path.startsWith(prefix));

/**
 * Whether a shared link lookup request should go to the shared routes: a GET for global reference data, or for NMS
 * measurement schemas the link doesn't have saved.
 */
export const isSharedLinkPublicLookupRequest = (path: string, method = 'GET') => {
  const key = toLookupKey(path);
  return (
    method.toUpperCase() === 'GET' &&
    (PUBLIC_LOOKUP_KEYS.has(key) ||
      (SHARED_FALLBACK_KEYS.has(key) && !Object.prototype.hasOwnProperty.call(savedLookups, key)))
  );
};

/**
 * Returns the saved response for a lookup request, or an empty response when nothing was saved for it.
 * Saved records aren't filtered by the request's search terms; callers match the values they need.
 */
export const getSharedLinkLookupResponse = (path: string): unknown => {
  const key = toLookupKey(path);
  const response = Object.prototype.hasOwnProperty.call(savedLookups, key) ? savedLookups[key] : EMPTY_RESPONSES[key];

  // copy so callers can't modify the saved lookups
  return response === undefined ? [] : JSON.parse(JSON.stringify(response));
};
